import {
  type AnnouncementAction,
  type AnnouncementMediaItem,
  type AnnouncementNarrowInput,
  type AnnouncementSettings,
  isAnnouncementActionType,
  isAnnouncementCommand,
  isAnnouncementMediaType,
  isAnnouncementSurface,
  isAnnouncementTone,
  isAnnouncementVariant,
} from '../constants';
import { ANNOUNCEMENT_COMMAND_ACTION_BUILDERS } from '../constants/announcementCommandParams';
import { getAnnouncementLinkHref } from './getAnnouncementLinkHref';

/**
 * Drops unknown surfaces, variants, tones, action types, and media types, plus actions whose url is not safe to link
 * or whose command params do not match that command.
 * `style.icon` is kept as a non-empty string; the host decides whether it can render it.
 * An unknown tone is omitted rather than guessed, including on placement rows.
 * Placement rows are keyed by surface; a surface named twice keeps the first known row.
 */
export const narrowAnnouncementSettings = (raw: AnnouncementNarrowInput): AnnouncementSettings => {
  const icon =
    typeof raw.settings.style.icon === 'string' && raw.settings.style.icon.length > 0
      ? raw.settings.style.icon
      : undefined;
  const tone = isAnnouncementTone(raw.settings.style.tone) ? raw.settings.style.tone : undefined;
  const image = typeof raw.settings.style.image === 'string' ? raw.settings.style.image : undefined;
  const placements: AnnouncementSettings['placements'] = {};

  for (const row of raw.placements) {
    if (!isAnnouncementSurface(row.surface) || placements[row.surface]) {
      continue;
    }

    if (!isAnnouncementVariant(row.settings.variant)) {
      continue;
    }

    const shortTitle = typeof row.settings.shortTitle === 'string' ? row.settings.shortTitle : undefined;
    const placementTone =
      typeof row.settings.tone === 'string' && isAnnouncementTone(row.settings.tone) ? row.settings.tone : undefined;

    placements[row.surface] = {
      variant: row.settings.variant,
      ...(shortTitle !== undefined ? { shortTitle } : {}),
      ...(placementTone ? { tone: placementTone } : {}),
    };
  }

  const actions: AnnouncementAction[] = [];

  for (const action of raw.settings.actions) {
    const look = action.type === 'command' ? 'button' : action.type;

    if (!isAnnouncementActionType(look) || typeof action.label !== 'string') {
      continue;
    }

    const url = typeof action.url === 'string' && action.url.length > 0 ? action.url : undefined;
    const command =
      typeof action.command === 'string' && isAnnouncementCommand(action.command) ? action.command : undefined;
    const hasUnknownCommand = typeof action.command === 'string' && action.command.length > 0 && !command;

    const hasUnsafeUrl = url !== undefined && getAnnouncementLinkHref(url) === null;

    if (hasUnsafeUrl || hasUnknownCommand || (url && command) || (!url && !command)) {
      continue;
    }

    if (command) {
      const commandAction = ANNOUNCEMENT_COMMAND_ACTION_BUILDERS[command](action.label, look, action.params);

      if (commandAction) {
        actions.push(commandAction);
      }

      continue;
    }

    if (url) {
      actions.push({ label: action.label, type: look, url });
    }
  }

  const media: AnnouncementMediaItem[] = [];

  for (const item of raw.settings.media ?? []) {
    if (typeof item.key === 'string' && item.key.length > 0 && item.type && isAnnouncementMediaType(item.type)) {
      media.push({ key: item.key, type: item.type });
    }
  }

  return {
    actions,
    media,
    placements,
    style: {
      ...(icon ? { icon } : {}),
      ...(image !== undefined ? { image } : {}),
      ...(tone ? { tone } : {}),
    },
  };
};
